import { TableOptions } from '@tanstack/react-table';
import { Spin } from 'antd';
import styled from 'styled-components';
import { useMediaQuery } from 'usehooks-ts';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { PartiallyOptional } from '@/shared/types';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import useTable from './useTable';
import { Backdrop } from '../backdrop';

export type AdaptiveTableProps<TData> = {
  options: PartiallyOptional<TableOptions<TData>, 'getCoreRowModel'>;
  placeholder?: React.ReactNode;
  adaptive?: boolean;
  layout?: 'auto' | 'fixed';
  breakpoint?: number;
  loading?: boolean;
};

export function AdaptiveTable<TData>({
  options,
  placeholder = <></>,
  adaptive = true,
  layout = 'fixed',
  breakpoint = 1024,
  loading = false,
}: AdaptiveTableProps<TData>) {
  const isMediumScreen = useMediaQuery(`(max-width: ${breakpoint}px)`);
  const table = useTable<TData>({ options });
  const noContent = !loading && !table.getCoreRowModel().rows.length;

  const { theme } = useThemeContext();
  const colors = themeConfig[theme].colors;

  const tableLayout =
    isMediumScreen && adaptive ? (
      <MobileLayout table={table} colors={colors} />
    ) : (
      <DesktopLayout table={table} layout={layout} colors={colors} />
    );

  return (
    <StyledTableWrapper>
      {loading && (
        <Backdrop>
          <Spin />
        </Backdrop>
      )}
      {noContent ? placeholder : tableLayout}
    </StyledTableWrapper>
  );
}

const StyledTableWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 20vh;
  overflow-x: auto;
`;
